import { useState, useEffect } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { score, ScoredMsg } from "./sentimentLocal";

interface UserData {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  name?: string;
  uid?: string;
}

interface MessageData {
  text?: string;
  createdAt: number;
  userId?: string;
  user?: UserData;
}

export const useChannelMessages = (channelId: string | null) => {
  const [msgs, setMsgs] = useState<ScoredMsg[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const rtdb = getDatabase();
  const firestore = getFirestore();

  useEffect(() => {
    if (!channelId) {
      setMsgs([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const r = ref(rtdb, `chats/${channelId}/messages`);

    const unsub = onValue(r, async (snap) => {
      const arr: ScoredMsg[] = [];
      const userCache: { [key: string]: string } = {};

      // Firebase realtime database returns an object, not an array
      const messagesObj = snap.val();
      if (!messagesObj) {
        setMsgs([]);
        setIsLoading(false);
        return;
      }

      // Convert object to array of messages
      const messages = Object.entries(messagesObj).map(([key, value]) => ({
        ...(value as MessageData),
        id: key
      }));

      for (const message of messages) {
        if (!message?.text) continue;
        
        let userName = 'Unknown User';
        const userId = message.userId || message.user?.uid;

        if (userId && !userCache[userId]) {
          try {
            // Try to get user from Firestore first
            const userRef = doc(firestore, 'users', userId);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
              const userData = userSnap.data();
              userName = `${userData.firstName} ${userData.lastName}`;
              userCache[userId] = userName;
            } else {
              // Fallback to message data if Firestore lookup fails
              if (message.user?.displayName) {
                userName = message.user.displayName;
              } else if (message.user?.name) {
                userName = message.user.name;
              }
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            // Fallback to message data if error occurs
            if (message.user?.displayName) {
              userName = message.user.displayName;
            } else if (message.user?.name) {
              userName = message.user.name;
            }
          }
        } else if (userId && userCache[userId]) {
          userName = userCache[userId];
        }

        arr.push(score(message.text, message.createdAt, userId || 'unknown', userName));
      }

      arr.sort((a, b) => a.createdAt - b.createdAt);
      setMsgs(arr);
      setIsLoading(false);
    });

    return () => unsub();
  }, [channelId, rtdb, firestore]);

  return { msgs, isLoading };
};
